import { useState, useEffect } from 'react';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerBody,
  DrawerFooter,
} from '../../../../../../components/ui/Drawer';
import { Button } from '../../../../../../components/ui/Button';
import { Input } from '../../../../../../components/ui/FormField';
import { useMarkAttendance } from '../../../../../../features/auctions/network';
import type { SubscriberListItem } from '../../../../../../features/schemes/network/schemes.api';

interface AuctionAttendanceDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  auctionId: string;
  subscribers: SubscriberListItem[];
  initialAttendees: string[];
}

export function AuctionAttendanceDrawer({
  isOpen,
  onClose,
  auctionId,
  subscribers,
  initialAttendees,
}: AuctionAttendanceDrawerProps) {
  const markAttendance = useMarkAttendance(auctionId);
  const [attendees, setAttendees] = useState<Record<string, boolean>>({});
  const [search, setSearch] = useState('');

  // Sync initial attendees when open
  useEffect(() => {
    if (isOpen) {
      const initialMap: Record<string, boolean> = {};
      subscribers.forEach((sub) => {
        initialMap[sub.id] = initialAttendees.includes(sub.id);
      });
      setAttendees(initialMap);
      setSearch('');
    }
  }, [isOpen, subscribers, initialAttendees]);

  const handleToggle = (id: string) => {
    setAttendees((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleSelectAll = () => {
    const updated: Record<string, boolean> = { ...attendees };
    filteredSubscribers.forEach((sub) => {
      updated[sub.id] = true;
    });
    setAttendees(updated);
  };

  const handleDeselectAll = () => {
    const updated: Record<string, boolean> = { ...attendees };
    filteredSubscribers.forEach((sub) => {
      updated[sub.id] = false;
    });
    setAttendees(updated);
  };

  const handleSave = async () => {
    const selectedIds = Object.keys(attendees).filter((id) => attendees[id]);
    try {
      await markAttendance.mutateAsync({ subscriberIds: selectedIds });
      onClose();
    } catch (error) {
      console.error('Failed to mark attendance', error);
    }
  };

  const filteredSubscribers = subscribers.filter((sub) => {
    const query = search.toLowerCase();
    const name = sub.customerName?.toLowerCase() || '';
    const ticket = (sub.ticketNumbers || []).join(',').toLowerCase();
    return name.includes(query) || ticket.includes(query);
  });

  return (
    <Drawer open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DrawerContent className="w-full max-w-md bg-surface-1">
        <DrawerHeader>
          <DrawerTitle>Manage Auction Attendance</DrawerTitle>
        </DrawerHeader>

        <DrawerBody className="flex flex-col space-y-4 overflow-hidden h-full">
          <p className="text-sm text-text-secondary">
            Select subscribers present for the auction.
          </p>

          <Input
            placeholder="Search by ticket or customer name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full"
          />

          <div className="flex gap-2 text-xs">
            <Button variant="secondary" onClick={handleSelectAll} size="sm" className="h-8">
              Select All Filtered
            </Button>
            <Button variant="secondary" onClick={handleDeselectAll} size="sm" className="h-8">
              Deselect All Filtered
            </Button>
          </div>

          <div className="flex-1 overflow-y-auto divide-y divide-border border border-border rounded-[6px] bg-surface-2 p-1">
            {filteredSubscribers.length === 0 ? (
              <div className="p-4 text-center text-sm text-text-muted">
                No subscribers found
              </div>
            ) : (
              filteredSubscribers.map((sub) => (
                <div
                  key={sub.id}
                  onClick={() => handleToggle(sub.id)}
                  className="flex items-center gap-3 p-3 hover:bg-surface-3 cursor-pointer transition-colors rounded-[4px]"
                >
                  <Input
                    type="checkbox"
                    checked={!!attendees[sub.id]}
                    onChange={() => {}} // Handled by parent click
                    className="h-4 w-4 rounded border-gray-300 text-accent-600 focus:ring-accent-500 cursor-pointer accent-accent-500"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-text-primary truncate">
                      {sub.customerName || 'Unknown Customer'}
                    </p>
                    <p className="text-xs text-text-muted">
                      Ticket: {(sub.ticketNumbers || []).join(',')} | Status: {sub.status}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </DrawerBody>

        <DrawerFooter className="border-t border-border p-4 bg-surface-2">
          <Button variant="secondary" onClick={onClose} disabled={markAttendance.isPending} className="flex-1">
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} loading={markAttendance.isPending} className="flex-1 bg-accent-500 text-white hover:bg-accent-600">
            Save Attendance
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
